import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tv, Plus, Search, Film, Star, Clock, Award, TrendingUp, Flame } from 'lucide-react';
import ShowDesktop from '../shows/ShowDesktop';

const ShowsTabDesktop = ({ userShows, onCreateShow, user, onOpenShow }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  
  const filteredShows = searchQuery 
    ? userShows.filter(show => 
        show.showName.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : userShows;
  
  // Calculate stats
  const totalEpisodes = userShows.reduce((total, show) => total + (show.episodeCount || 0), 0);
  const totalShows = userShows.length;
  const averageRating = userShows.length > 0 
    ? userShows.reduce((total, show) => {
        if (show.showPossibleScore > 0) {
          return total + ((show.showEarnedScore / show.showPossibleScore) * 10);
        }
        return total;
      }, 0) / userShows.filter(show => show.showPossibleScore > 0).length
    : 0;
  const highestStreak = userShows.length > 0
    ? Math.max(...userShows.map(show => show.showStreak || 0))
    : 0;
  
  return (
    <div style={{ width: '100%', color: 'white', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '1.5rem'
      }}>
        <h2 style={{ margin: 0, fontSize: '1.5rem' }}>My Shows</h2>
        <button
          onClick={onCreateShow}
          style={{
            backgroundColor: 'rgb(255,255,255,.8)',
            color: '#6600cc',
            border: 'none',
            borderRadius: '0.5rem',
            marginLeft: '1rem',
            padding: '0.5rem 1rem',
            display: 'flex',
            fontWeight: '600',
            alignItems: 'center',
            gap: '0.5rem',
            cursor: 'pointer',
            fontSize: '0.9rem'
          }}
        >
          <Plus size={18} />
          Create Show
        </button>
      </div>
      
     
      
      {userShows.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>
          <Tv size={48} style={{ opacity: 0.5, marginBottom: '1rem' }} />
          <p>You haven't created any shows yet</p>
          <button
            onClick={onCreateShow}
            style={{
              backgroundColor: 'rgb(255,255,255,.8)',
              color: '#6600cc',
              border: 'none',
              borderRadius: '0.5rem',
              marginLeft: '1rem',
              padding: '0.5rem 1rem',
              display: 'flex',
              fontWeight: '600',
              alignItems: 'center',
              gap: '0.5rem',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            <Plus size={18} />
            Create Show
          </button>
        </div>
      ) : filteredShows.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>
          No shows match your search
        </div>
      ) : (
        <div style={{ display: 'flex', gap: '2rem' }}>
          {/* Shows grid */}
          <div style={{ 
            flex: '1 1 70%',
            display: 'flex', 
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: '1.5rem' 
          }}>
            {filteredShows.map((show, index) => (
              <div
                key={show.id}
                className="tab-item"
                style={{
                  '--item-index': index,
                  cursor: 'pointer',
                  borderRadius: '0.5rem',
                  overflow: 'hidden',
                  flex: '0 0 7.5rem',
                  transition: 'transform 0.2s',
                  ':hover': {
                    transform: 'translateY(-2px)'
                  }
                }}
                onClick={() => onOpenShow(show.id)}
              >
                <div style={{
                  width: '100%',
                  aspectRatio: '2/3',
                  position: 'relative'
                }}>
                  {show.showImage ? (
                    <img
                      src={show.showImage}
                      alt={show.showName}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '0.5rem'
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '100%',
                      height: '100%',
                      backgroundColor: '#333',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '0.5rem'
                    }}>
                      <Film size={32} color="#666" />
                    </div>
                  )}
                  
                  {/* Overlay gradient */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.8) 100%)',
                    borderRadius: '0.5rem'
                  }} />
                  
                  {/* Tags */}
                  <div style={{
                    position: 'absolute',
                    top: '0.5rem',
                    left: '0.5rem',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.25rem'
                  }}>
                    {show.showStreak > 0 && (
                      <span style={{
                        backgroundColor: '#6600CC',
                        color: 'white',
                        fontSize: '0.7rem',
                        padding: '0.2rem 0.4rem',
                        borderRadius: '0.25rem',
                        fontWeight: 'bold'
                      }}>
                        {show.showStreak} DAY STREAK
                      </span>
                    )}
                    
                    {show.latestEpisodeDate && new Date(show.latestEpisodeDate.seconds * 1000) > new Date(Date.now() - 3 * 24 * 60 * 60 * 1000) && (
                      <span style={{
                        backgroundColor: '#6600CC',
                        color: 'white',
                        fontSize: '0.7rem',
                        padding: '0.2rem 0.4rem',
                        borderRadius: '0.25rem',
                        fontWeight: 'bold'
                      }}>
                        NEW EPISODE
                      </span>
                    )}
                  </div>
                  
                  {/* Show title */}
                  <div style={{
                    position: 'absolute',
                    bottom: '0.5rem',
                    left: '0.5rem',
                    right: '0.5rem'
                  }}>
                    <h3 style={{
                      margin: '0',
                      fontSize: '0.9rem',
                      fontWeight: 'bold',
                      color: 'white',
                      textShadow: '0 1px 3px rgba(0,0,0,0.8)'
                    }}>
                      {show.showName}
                    </h3>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Stats panel */}
          <div style={{ 
            backgroundColor: 'rgba(255,255,255,0.09)',
            borderRadius: '0.5rem',
            padding: '2rem',
            height: '7.5rem'
          }}>
            <h3 style={{ 
              marginTop: '-1rem',
              fontSize: '1.2rem',
              fontWeight: 'bold'
            }}>
              Stats
            </h3>
            
            <div style={{ display: 'flex',  gap: '1.5rem' }}>

<div style={{width: '12rem', gap: '1rem', display: 'flex', flexDirection: 'column'}}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{ 
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Tv size={20} color="white" />
                </div>
                
                  <div style={{ fontSize: '0.9rem',  width: '4rem', lineHeight: '.9', fontWeight: '600', color: 'lightgrey'}}>Total Shows</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold' ,marginLeft: 'auto' }}>{totalShows}</div>
        
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{ 
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Clock size={20} color="white" />
                </div>
                <div style={{ fontSize: '0.9rem',  width: '4rem', lineHeight: '.9', fontWeight: '600', color: 'lightgrey'}}>Total Episodes</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold',marginLeft: 'auto'  }}>{totalEpisodes}</div>
            
              </div>
              </div>
          <div style={{height: '6rem', width: '4px', background: 'grey', borderRadius: '10px', opacity: '50%'}}/>
<div style={{width: '14rem', gap: '1rem', display: 'flex', flexDirection: 'column'}}>
              {averageRating > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ 
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Star size={20} color="white" />
                  </div>
                  <div style={{ fontSize: '0.9rem',  width: '6rem', lineHeight: '.9', fontWeight: '600', color: 'lightgrey'}}>Average Rating (ISDb)</div>
               
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold' , marginLeft: 'auto' }}>{averageRating.toFixed(1)}</div>
                  </div>
              
              )}
              
              {highestStreak > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ 
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Flame size={20} color="white" />
                  </div>
                  <div style={{ fontSize: '0.9rem',  width: '4rem', lineHeight: '.9', fontWeight: '600', color: 'lightgrey'}}>Highest Streak</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', marginLeft: 'auto' }}>{highestStreak} days</div>
                  </div>
           
              )}
            </div>
          </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShowsTabDesktop; 